import { useState, useRef } from 'react';
import { cloneDefaultClauses } from '../../rentals/utils/contractClauses';

/**
 * Per-agency editor for the contract VERSO clauses.
 *
 * Reads/writes `local.contractClauses` (an array). When the agency has never
 * customized, the array is absent and the printed contract falls back to the
 * system defaults; the "Charger les clauses par défaut" button seeds an
 * editable copy. All data lives in the agency's settings → fully isolated.
 */
export default function ContractClausesSettings({ local, setLocal, isAr = false }) {
  const t = (fr, ar) => (isAr ? ar : fr);
  const clauses = Array.isArray(local.contractClauses) ? local.contractClauses : null;
  const dragIndex = useRef(null);
  const [dragOver, setDragOver] = useState(null);

  const setClauses = (next) => setLocal({ ...local, contractClauses: next });

  const seedDefaults = () => setClauses(cloneDefaultClauses());
  const clearCustom = () => setLocal({ ...local, contractClauses: undefined });

  const update = (i, patch) => {
    const next = clauses.map((c, idx) => (idx === i ? { ...c, ...patch } : c));
    setClauses(next);
  };
  const remove = (i) => setClauses(clauses.filter((_, idx) => idx !== i));
  const addCustom = () => setClauses([
    ...(clauses || []),
    { id: `custom_${Date.now()}`, enabled: true, frTitle: '', fr: '', arTitle: '', ar: '' },
  ]);

  const move = (from, to) => {
    if (from === to || from == null || to == null) return;
    const next = [...clauses];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    setClauses(next);
  };

  // ── Not yet customized: show the default-state card ──
  if (!clauses) {
    return (
      <div className="space-y-3">
        <div>
          <h3 className="font-black text-gray-900 dark:text-white flex items-center gap-2">
            📜 {t('Clauses du contrat (VERSO)', 'بنود العقد (الظهر)')}
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {t('Actuellement, vos contrats utilisent les clauses standard du système. Chargez-les pour les modifier, supprimer, réorganiser ou ajouter vos propres clauses.',
               'حالياً تستعمل عقودك البنود القياسية للنظام. حمّلها لتعديلها أو حذفها أو إعادة ترتيبها أو إضافة بنودك الخاصة.')}
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs text-green-700 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg px-3 py-2">
          ✅ {t('Clauses standard actives (par défaut)', 'البنود القياسية مفعّلة (افتراضي)')}
        </div>
        <button
          onClick={seedDefaults}
          className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-sm"
        >
          ✏️ {t('Personnaliser les clauses', 'تخصيص البنود')}
        </button>
      </div>
    );
  }

  // ── Editing mode ──
  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-black text-gray-900 dark:text-white flex items-center gap-2">
            📜 {t('Clauses du contrat (VERSO)', 'بنود العقد (الظهر)')}
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {t('Glissez-déposez pour réorganiser. Ces clauses remplacent les clauses par défaut sur vos contrats.',
               'اسحب وأفلت لإعادة الترتيب. هذه البنود تعوّض البنود الافتراضية في عقودك.')}
          </p>
        </div>
        <span className="text-xs font-bold text-blue-700 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-3 py-1.5">
          {clauses.length} {t('clauses', 'بند')}
        </span>
      </div>

      <div className="space-y-2">
        {clauses.map((c, i) => (
          <div
            key={c.id || i}
            draggable
            onDragStart={() => { dragIndex.current = i; }}
            onDragOver={(e) => { e.preventDefault(); setDragOver(i); }}
            onDragLeave={() => setDragOver(null)}
            onDrop={(e) => { e.preventDefault(); move(dragIndex.current, i); dragIndex.current = null; setDragOver(null); }}
            className={`border rounded-xl p-3 bg-white dark:bg-gray-800 transition ${
              dragOver === i ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200 dark:border-gray-700'
            } ${c.enabled === false ? 'opacity-60' : ''}`}
          >
            <div className="flex items-center gap-2 mb-2">
              <span className="cursor-grab text-gray-400 select-none" title={t('Glisser pour déplacer', 'اسحب للتحريك')}>⠿</span>
              <span className="text-xs font-black text-gray-400">{t('Article', 'الفصل')} {i + 1}</span>
              <div className="flex-1" />
              {/* enable/disable */}
              <label className="flex items-center gap-1.5 cursor-pointer text-xs text-gray-600 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={c.enabled !== false}
                  onChange={(e) => update(i, { enabled: e.target.checked })}
                  className="accent-green-600"
                />
                {c.enabled !== false ? t('Activée', 'مفعّل') : t('Désactivée', 'معطّل')}
              </label>
              <button
                onClick={() => remove(i)}
                className="text-red-500 hover:text-red-700 text-sm font-bold px-1"
                title={t('Supprimer', 'حذف')}
              >
                🗑️
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {/* FR */}
              <div className="space-y-1">
                <input
                  type="text"
                  value={c.frTitle || ''}
                  onChange={(e) => update(i, { frTitle: e.target.value })}
                  placeholder={t('Titre (Français)', 'العنوان (فرنسية)')}
                  className="w-full px-2.5 py-1.5 text-sm font-bold border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg outline-none focus:ring-2 focus:ring-blue-400"
                />
                <textarea
                  value={c.fr || ''}
                  onChange={(e) => update(i, { fr: e.target.value })}
                  placeholder={t('Contenu (Français) — une ligne par puce avec •', 'المحتوى (فرنسية)')}
                  rows={4}
                  className="w-full px-2.5 py-1.5 text-xs border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>
              {/* AR */}
              <div className="space-y-1" dir="rtl">
                <input
                  type="text"
                  value={c.arTitle || ''}
                  onChange={(e) => update(i, { arTitle: e.target.value })}
                  placeholder="العنوان (عربية)"
                  className="w-full px-2.5 py-1.5 text-sm font-bold border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg outline-none focus:ring-2 focus:ring-blue-400"
                />
                <textarea
                  value={c.ar || ''}
                  onChange={(e) => update(i, { ar: e.target.value })}
                  placeholder="المحتوى (عربية) — سطر لكل نقطة مع •"
                  rows={4}
                  className="w-full px-2.5 py-1.5 text-xs border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={addCustom}
          className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold text-sm"
        >
          ➕ {t('Ajouter une clause', 'إضافة بند')}
        </button>
        <button
          onClick={() => { if (confirm(t('Réinitialiser aux clauses par défaut ? Vos modifications seront perdues.', 'إعادة التعيين للبنود الافتراضية؟ ستفقد تعديلاتك.'))) seedDefaults(); }}
          className="px-4 py-2 bg-amber-50 border border-amber-200 hover:bg-amber-100 text-amber-700 rounded-xl font-bold text-sm"
        >
          ↺ {t('Réinitialiser', 'إعادة تعيين')}
        </button>
        <button
          onClick={() => { if (confirm(t('Revenir aux clauses standard du système (supprime votre personnalisation) ?', 'العودة للبنود القياسية (يحذف تخصيصك)؟'))) clearCustom(); }}
          className="px-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 hover:bg-gray-100 text-gray-600 dark:text-gray-300 rounded-xl font-bold text-sm"
        >
          {t('Utiliser les clauses système', 'استعمال بنود النظام')}
        </button>
      </div>

      <p className="text-[11px] text-gray-400">
        💡 {t('N\'oubliez pas de cliquer sur « Enregistrer » en bas de la page pour sauvegarder vos clauses.',
              'لا تنسَ الضغط على « حفظ » أسفل الصفحة لحفظ بنودك.')}
      </p>
    </div>
  );
}
